/**
 * TimeTravelPill — small "View as of" date selector in the Header.
 *
 * Only visible on CEO dashboards (currently hardcoded to /dashboard;
 * the Header decides whether to mount it based on route).
 *
 * Toggles between TIME_STOPS — Today / 30 / 60 / 90 / 180 / 365 days
 * ago. Other dashboards / pages are unaffected — only consumers of
 * `useTimeTravel()` see the rewind.
 */
import React, { useState, useRef, useEffect } from "react";
import { History, Check, ChevronDown } from "lucide-react";
import { useTimeTravel, TIME_STOPS } from "@/contexts/TimeTravelContext";
import { cn } from "@/lib/utils";

export default function TimeTravelPill() {
  const { viewAsOf, setViewAsOf, label } = useTimeTravel();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = viewAsOf !== 0;

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="time-travel-pill">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md text-meta font-semibold border transition-colors",
          active
            ? "bg-gold-300/20 border-gold-600 text-gold-700 hover:bg-gold-300/30"
            : "bg-white border-border-subtle text-ink-secondary hover:bg-inset hover:text-ink-primary",
        )}
        data-testid="time-travel-trigger"
        title="Rewind dashboards to view how the estate looked on a past date"
      >
        <History size={12} />
        <span className="hidden md:inline">View as of</span>
        <span className={cn("font-mono", active && "text-gold-700")}>{label}</span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[220px] rounded-md border border-border-subtle bg-raised shadow-e3 anim-fade-up z-50 overflow-hidden"
          data-testid="time-travel-menu"
        >
          <div className="px-3 py-2 border-b border-border-subtle bg-inset/40">
            <p className="eyebrow">Rewind estate to</p>
          </div>
          <ul className="py-1">
            {TIME_STOPS.map((s) => (
              <li key={s.value}>
                <button
                  type="button"
                  onClick={() => { setViewAsOf(s.value); setOpen(false); }}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                    s.value === viewAsOf
                      ? "bg-gold-300/15 text-gold-700 font-semibold"
                      : "text-ink-primary hover:bg-inset",
                  )}
                  data-testid={`time-travel-stop-${s.value}`}
                >
                  {s.value === viewAsOf ? (
                    <Check size={12} className="text-gold-700" />
                  ) : (
                    <span className="w-3" />
                  )}
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 border-t border-border-subtle text-[10px] text-ink-disabled">
            Demo mode: scales KPIs by the maturity curve.
          </div>
        </div>
      )}
    </div>
  );
}
